/**
 * How is this flat roof built?
 *
 * Asked before a TPO estimate is generated, because the answers change which
 * materials are ordered, what the crew is paid and what the customer reads —
 * not just how much of each. See packages/core/src/flatRoof.ts for the rules
 * and the quotes behind them.
 *
 * Two of the answers are presets rather than questions in their own right. New
 * construction and a flute fill each describe a whole way of building, so
 * ticking one fills in the rest of the form; every field it sets can still be
 * changed afterwards, because a preset is a starting point and not a lock.
 */

import {
  INSULATIONS,
  TERMINATIONS,
  VERSICO_STACK_LABEL,
  flatRoofMeasurementWarnings,
  flatRoofWarnings,
  fluteFillAnswers,
  newConstructionAnswers,
  versicoAnswers,
  type BarFastener,
  type FlatRoofAnswers,
  type Termination,
} from "@roof-hawk/core";

import { t } from "../../i18n";

const TERMINATION_LABEL: Record<Termination, string> = {
  bar: "Termination bar",
  kynar: "Kynar drip edge (adds cover tape and primer)",
  tpo_metal: "TPO coated metal (heat welded, no tape or primer)",
};

/** What the bar screws into, which decides what holds it. */
const BAR_FASTENER_LABEL: Record<BarFastener, string> = {
  masonry: 'Masonry — 1/4" x 1-1/2" masonry anchors',
  wood: 'Wood — 1-1/4" metal to wood screws',
};

/** A roof this estimate will be generated against, as this form reads one. */
export interface FlatRoofMeasurement {
  flat_squares?: unknown;
  squares?: unknown;
  wall_length_lf?: unknown;
}

export function FlatRoofQuestions({
  answers,
  onChange,
  roofs = [],
}: {
  answers: FlatRoofAnswers;
  onChange: (answers: FlatRoofAnswers) => void;
  /**
   * The measured roofs, read only to say what is missing.
   *
   * Nothing here edits a measurement — that is the measurement screen's job.
   * What it can do is stop a rep generating an order whose quantities all come
   * out at nothing, which is what happened on job 1060 and read as a working
   * order until somebody counted the lines.
   */
  roofs?: readonly FlatRoofMeasurement[];
}) {
  const set = <K extends keyof FlatRoofAnswers>(
    key: K,
    value: FlatRoofAnswers[K],
  ) => onChange({ ...answers, [key]: value });

  const warnings = [
    ...flatRoofMeasurementWarnings(answers, roofs),
    ...flatRoofWarnings(answers),
  ];

  /*
   * Ticking a preset rewrites the form; unticking it puts the answers back to
   * an ordinary reroof rather than leaving the preset's choices behind under a
   * heading that no longer says so.
   */
  const preset = (
    which: "newConstruction" | "fluteFill",
    on: boolean,
  ): void => {
    if (!on) {
      onChange({ ...answers, [which]: false });
      return;
    }
    onChange(
      which === "newConstruction"
        ? newConstructionAnswers()
        : fluteFillAnswers(),
    );
  };

  /*
   * Versico is a preset too, but a narrower one: it sets the insulation the
   * Valvoline job used and leaves everything else alone. Unticking it drops
   * only the flag — the board it chose stays, because by then it is as good an
   * answer as any and changing it back under the rep would be worse.
   */
  const setVersico = (on: boolean): void =>
    onChange(on ? versicoAnswers(answers) : { ...answers, versico: false });

  return (
    <div className="flatq">
      <h3>{t("How is this roof built?")}</h3>

      <label className="flatq-check">
        <input
          type="checkbox"
          checked={answers.newConstruction}
          onChange={(e) => preset("newConstruction", e.target.checked)}
        />
        {t("New construction — nothing to tear off or roof over")}
      </label>

      <label className="flatq-check">
        <input
          type="checkbox"
          checked={answers.fluteFill}
          onChange={(e) => preset("fluteFill", e.target.checked)}
        />
        {t("TPO flute fill over a metal deck")}
      </label>

      {!answers.newConstruction && !answers.fluteFill && (
        <label className="flatq-row">
          <span>{t("Layers to tear off")}</span>
          <input
            type="number"
            min={0}
            value={answers.tearoffLayers}
            onChange={(e) =>
              set("tearoffLayers", Math.max(0, Number(e.target.value) || 0))
            }
          />
          <span className="flatq-hint">
            {t(
              "Each layer is another $20 a square to the crew, and it sizes the dumpster.",
            )}
          </span>
        </label>
      )}

      <label className="flatq-row">
        <span>{t("Insulation")}</span>
        <select
          value={answers.insulation}
          onChange={(e) =>
            set("insulation", e.target.value as FlatRoofAnswers["insulation"])
          }
        >
          {INSULATIONS.map((i) => (
            <option key={i.key} value={i.key}>
              {t(i.label)}
            </option>
          ))}
        </select>
        <select
          value={answers.insulationLayers}
          onChange={(e) => set("insulationLayers", Number(e.target.value))}
        >
          <option value={1}>{t("1 layer")}</option>
          <option value={2}>{t("2 layers")}</option>
        </select>
      </label>

      {/* Outside the label on purpose. The other hints describe the field they
          sit in; this one announces a second material, and inside the label it
          would become part of the insulation field's accessible name. */}
      {answers.versico && (
        <p className="flatq-row">
          <span />
          <span className="flatq-hint">+ {t(VERSICO_STACK_LABEL)}</span>
        </p>
      )}

      <label className="flatq-row">
        <span>{t("Insulation is")}</span>
        <select
          value={answers.isoAttachment}
          onChange={(e) =>
            set(
              "isoAttachment",
              e.target.value as FlatRoofAnswers["isoAttachment"],
            )
          }
        >
          <option value="mechanical">
            {t("Mechanically attached — screwed through metal or wood deck")}
          </option>
          <option value="adhered">
            {t("Adhered — glued, for a concrete deck you cannot screw")}
          </option>
        </select>
      </label>

      <label className="flatq-row">
        <span>{t("Membrane is")}</span>
        <select
          value={answers.tpoAttachment}
          onChange={(e) =>
            set(
              "tpoAttachment",
              e.target.value as FlatRoofAnswers["tpoAttachment"],
            )
          }
        >
          <option value="screwed">{t("Mechanically fastened")}</option>
          <option value="adhered">{t("Fully adhered")}</option>
        </select>
      </label>

      {(answers.isoAttachment === "mechanical" ||
        answers.tpoAttachment === "screwed") && (
        <label className="flatq-row">
          <span>{t("Screw length")}</span>
          <input
            type="number"
            min={0}
            step={0.5}
            value={answers.screwInches}
            onChange={(e) => set("screwInches", Number(e.target.value) || 0)}
          />
          <span className="flatq-hint">
            {t(
              'inches — has to pass the deck and come out 1" the far side, and on metal decking that is 1" past the low rib.',
            )}
          </span>
        </label>
      )}

      <label className="flatq-check">
        <input
          type="checkbox"
          checked={answers.wallInsulation}
          onChange={(e) => set("wallInsulation", e.target.checked)}
        />
        {t("Insulation up the parapet walls as well as the roof")}
      </label>

      <label className="flatq-row">
        <span>{t("Terminated with")}</span>
        <select
          value={answers.termination}
          onChange={(e) => set("termination", e.target.value as Termination)}
        >
          {TERMINATIONS.map((term) => (
            <option key={term} value={term}>
              {t(TERMINATION_LABEL[term])}
            </option>
          ))}
        </select>
      </label>

      {answers.termination === "bar" && (
        <label className="flatq-row">
          <span>{t("Bar fastens into")}</span>
          <select
            value={answers.barFastener}
            onChange={(e) => set("barFastener", e.target.value as BarFastener)}
          >
            {(Object.keys(BAR_FASTENER_LABEL) as BarFastener[]).map((f) => (
              <option key={f} value={f}>
                {t(BAR_FASTENER_LABEL[f])}
              </option>
            ))}
          </select>
        </label>
      )}

      {answers.termination !== "bar" && (
        <label className="flatq-row">
          <span>{t("Drip edge girth")}</span>
          <input
            type="number"
            min={0}
            value={answers.terminationGirthInches}
            onChange={(e) =>
              set("terminationGirthInches", Number(e.target.value) || 0)
            }
          />
          <span className="flatq-hint">
            {t("inches, every bend added up.")}{" "}
            {sheetHint(answers.terminationGirthInches)}
          </span>
        </label>
      )}

      <label className="flatq-check">
        <input
          type="checkbox"
          checked={answers.coping}
          onChange={(e) => set("coping", e.target.checked)}
        />
        {t("Coping on the parapet walls")}
      </label>

      {answers.coping && (
        <label className="flatq-row">
          <span>{t("Coping girth")}</span>
          <input
            type="number"
            min={0}
            value={answers.copingGirthInches}
            onChange={(e) =>
              set("copingGirthInches", Number(e.target.value) || 0)
            }
          />
          <span className="flatq-hint">
            {t("inches, every bend added up.")}{" "}
            {sheetHint(answers.copingGirthInches)}
          </span>
        </label>
      )}

      <label className="flatq-check">
        <input
          type="checkbox"
          checked={answers.scuppers}
          onChange={(e) => set("scuppers", e.target.checked)}
        />
        {t("Scuppers — adds a sheet of TPO coated metal")}
      </label>

      {/* A different bill of materials, not a different price for the same one:
          the membrane, detail membrane, adhesive and fasteners are the
          system's own, and the warranty depends on them being used together. */}
      <label className="flatq-check">
        <input
          type="checkbox"
          checked={answers.versico}
          onChange={(e) => setVersico(e.target.checked)}
        />
        {t("Versico job — uses the Versico membrane, adhesive and fasteners")}
      </label>

      {warnings.map((w) => (
        <p key={w.key} className="flatq-warn" role="alert">
          {t(w.message)}
        </p>
      ))}
    </div>
  );
}

/**
 * What a girth yields, said back as the rep types it.
 *
 * The arithmetic is not obvious and the answer moves in steps — 12 inches gives
 * 40 feet a sheet and 13 gives 30 — so showing it is the difference between a
 * number somebody chose and a number somebody guessed.
 */
function sheetHint(girthInches: number): string {
  const strips = Math.floor(48 / (girthInches || 0));
  if (!Number.isFinite(strips) || strips < 1) return "";
  return strips === 1
    ? t("1 strip per 4x10 sheet, {feet} feet.", { feet: strips * 10 })
    : t("{n} strips per 4x10 sheet, {feet} feet.", {
        n: strips,
        feet: strips * 10,
      });
}
